/**
=========================================================
* Material Dashboard 2 PRO React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect, useContext } from "react";
import { useNavigate, useLocation } from "react-router-dom";

// @mui material components
import { Grid, Icon, Divider } from "@mui/material";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";

// Material Dashboard 2 PRO React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

// import Header from "layouts/pages/profile/components/Header"; Commenting for Issue: #53
import CourseCard from "examples/Cards/CourseCard";

// Services
import AuthService from "services/auth-service";
import CrudService from "services/cruds-service";
import { set } from "date-fns";
import { analytics } from "../../dashboards/analytics/analytics";
import { events } from "../../dashboards/analytics/events";
import { AuthContext } from "context";

function useDebounce(value, delay = 400) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);           // cancel on next keystroke
  }, [value, delay]);
  return debounced;
}

function MyLibrary() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 500);
  const [availableCourses, setAvailableCourses] = useState([]);
  const [filteredCourses, setFilteredCourses] = useState(availableCourses);

  const [user, setUser] = useState({
    id: "",
    name: "",
    email: "",
    password: "",
    newPassword: "",
    currentPassword: "",
    confirmPassword: "",
    profile_image: "",
  });
  const { getCurrentUser } = useContext(AuthContext);

  useEffect(() => {
    const start_time = Date.now();
    (async () => {
      try {
        const response = await AuthService.getProfile();
        setUser((prevUser) => ({
          ...prevUser,
          id: response.data.id,
          name: response.data.attributes.name,
          email: response.data.attributes.email,
          profile_image: response.data.attributes.profile_image,
          currentPassword: "",
          newPassword: "",
          confirmPassword: "",
        }));

        analytics.track(events.VIEW_PAGE, { page: "my-library", userId: response.data.id });

        let courses = await CrudService.getCoursesSummaries();

        const availableCourses = await CrudService.getAvailableCourses(
          response.data.id
        );

        let purchasedCourses = courses
          .filter(course => availableCourses.includes(course._id))


        //Sort Courses.data in ascending order wrt time creation
        purchasedCourses.sort((a, b) => {
          return new Date(b.creationDate) - new Date(a.creationDate);
        });

        setAvailableCourses(purchasedCourses);
        setFilteredCourses(purchasedCourses);
      } catch (error) {
        console.log(error);
        alert(
          "Error fetching courses. Please refresh the page or try again later."
        );
      }

    })();
    async function getUserId() {
      return await getCurrentUser();
    }
    return () => {
      getUserId().then((user) => {
        analytics.track(events.PAGE_LEFT, { page: "my-library", userId: user.id, start_time: start_time, end_time: Date.now() });
      });
    }

  }, []);



  useEffect(() => {
    if (debouncedSearch && debouncedSearch.length >= 3) {
      analytics.track(events.SEARCH_FILTER_APPLIED, {
        query: debouncedSearch,
        results: filteredCourses.length,
      });
    }
  }, [debouncedSearch]);


  useEffect(() => {
    if (search === undefined) {
      return;
    }
    setSearch(search);
    // Create a shallow copy of the available courses
    const coursesCopy = [...availableCourses];
    let searchLowerCase = search.toLowerCase();

    const filteredCourses = coursesCopy.filter((course) => {
      const matchessearch =
        course.app_name.toLowerCase().includes(searchLowerCase) ||
        course.short_description
          .toLowerCase()
          .includes(searchLowerCase);

      // Only include the course if it matches both the search text and the active filters
      return matchessearch;
    });

    if (search === "" && filteredCourses.length === 0) {
      setSearch(search);
      setFilteredCourses(availableCourses);
      return;
    }

    setFilteredCourses(filteredCourses);
  }, [search]);

  // Function to explore a course
  const exploreCard = (course_id) => {
    navigate("/course-content/" + course_id);
  };

  return (
    <DashboardLayout id={1}>
      <DashboardNavbar />
      {/* <Header user={user} /> */}
      <MDTypography variant="h3" mt={2}>
        My Library
      </MDTypography>
      <Divider />
      {/* Search functionality */}
      <MDBox>
        <Grid container spacing={2}>
          <Grid item xs={12} lg={10}>
            <MDInput
              placeholder="Search..."
              value={search}
              size="small"
              fullWidth
              onChange={(e) => setSearch(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} lg={2}>
            <MDButton variant="outlined" fullWidth color="info">
              <Icon>search</Icon>&nbsp; Search
            </MDButton>
          </Grid>
        </Grid>
      </MDBox>

      {/* Display the courses */}
      <MDBox mt={2} mb={4}>
        <Grid container spacing={3}>
          {(filteredCourses.length == 0 || availableCourses.length === 0) && (
            <MDTypography variant="h5" color="secondary" ml={3} mt={2}>
              No courses found
            </MDTypography>
          )}
          {filteredCourses.map((course) => (
            <Grid item xs={12} md={6} lg={4}>
              <MDBox mt={3}>
                <CourseCard
                  image={course.app_image_location}
                  title={course.app_name}
                  description={course.short_description}
                  label="Explore"
                  action={<></>}
                  exploreCard={() => exploreCard(course._id)}
                  moduleCount={course.num_modules}
                  height="auto"
                  purchased={true}
                  externalLink={course.external_link}
                />
              </MDBox>
            </Grid>
          ))}
        </Grid>
      </MDBox>
    </DashboardLayout>
  );
}

export default MyLibrary;